import { ErrorResponse, fetchAPI } from '@quickbit/backoffice-core-frontend';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { Platform } from 'tabs/types';

type PlatformWithApiKey = Platform & { apiKey: string };

const queryKeys = {
  all: () => ['platforms.all'] as const,
  getPlatformList: () => [...queryKeys.all(), 'getPlatformList'] as const,
  addPlatform: () => [...queryKeys.all(), 'addPlatform'] as const,
  updatePlatform: () => [...queryKeys.all(), 'updatePlatform'] as const,
  linkMerchantToPlatform: () =>
    [...queryKeys.all(), 'linkMerchantToPlatform'] as const,
  deletePendingMerchant: () =>
    [...queryKeys.all(), 'deletePendingMerchant'] as const,
  deletePlatform: () => [...queryKeys.all(), 'deletePlatform'] as const,
  createPendingMerchant: () =>
    [...queryKeys.all(), 'createPendingMerchant'] as const,
};

const getPlatformList = async () => {
  const { platforms } = await fetchAPI<{ platforms: Platform[] }>(
    'v1/platforms'
  );
  return platforms.map((platform) => ({
    ...platform,
    ...(platform.deletedAt ? { deletedAt: new Date(platform.deletedAt) } : {}),
  }));
};

const addPlatform = (platform: Platform) => {
  return fetchAPI<Platform & { apiKey: string }>('v1/platforms', {
    method: 'POST',
    body: {
      ...platform,
    },
  });
};

const updatePlatform = (platform: Platform) => {
  return fetchAPI(`v1/platforms/${platform.uuid}`, {
    method: 'PUT',
    body: { ...platform },
  });
};

const linkMerchantToPlatform = (platformId: string, merchantId: string) => {
  return fetchAPI(`v1/platforms/${platformId}/link-merchant`, {
    method: 'POST',
    body: { merchantId },
  });
};

const deletePlatform = (platformId: string) => {
  return fetchAPI(`v1/platforms/${platformId}`, {
    method: 'DELETE',
  });
};

const createPendingMerchant = (platformId: string) => {
  return fetchAPI<{ merchantId: string }>(
    `v1/platforms/${platformId}/merchants`,
    {
      method: 'POST',
    }
  );
};

const deletePendingMerchant = (merchantId: string) => {
  return fetchAPI(`v1/pending-merchants/${merchantId}`, {
    method: 'DELETE',
  });
};

interface PlatformApi {
  getPlatformList: () => UseQueryResult<Platform[]>;
  addPlatform: (
    onSuccess?: (platform: PlatformWithApiKey) => void
  ) => UseMutationResult<PlatformWithApiKey, ErrorResponse, Platform>;
  updatePlatform: (
    onSuccess?: (platform: Platform) => void
  ) => UseMutationResult<Platform, ErrorResponse, Platform>;
  linkMerchantToPlatform: (
    onSuccess?: (platformId: string) => void
  ) => UseMutationResult<
    string,
    ErrorResponse,
    { platformId: string; merchantId: string }
  >;
  deletePendingMerchant: (
    onSuccess?: () => void
  ) => UseMutationResult<void, ErrorResponse, { merchantId: string }>;
  deletePlatform: (
    onSuccess?: () => void
  ) => UseMutationResult<unknown, ErrorResponse, string>;
  createPendingMerchant: (
    onSuccess?: ({
      merchantId,
      platformId,
    }: {
      merchantId: string;
      platformId: string;
    }) => void
  ) => UseMutationResult<
    { merchantId: string; platformId: string },
    ErrorResponse,
    string
  >;
}

export const usePlatformApi = (): PlatformApi => {
  const queryClient = useQueryClient();

  const useGetPlatformList = () => {
    return useQuery<Platform[]>({
      queryKey: queryKeys.getPlatformList(),
      queryFn: getPlatformList,
    });
  };

  const useAddPlatform = (
    onSuccess?: (platform: PlatformWithApiKey) => void
  ) => {
    return useMutation<PlatformWithApiKey, ErrorResponse, Platform>({
      mutationKey: queryKeys.addPlatform(),
      mutationFn: (platform: Platform) => addPlatform(platform),
      onSuccess: (platform) => {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.all(),
        });
        onSuccess && onSuccess(platform);
      },
    });
  };

  const useUpdatePlatform = (onSuccess?: (platform: Platform) => void) => {
    return useMutation<Platform, ErrorResponse, Platform>({
      mutationKey: queryKeys.updatePlatform(),
      mutationFn: async (platform: Platform) => {
        await updatePlatform(platform);
        return platform;
      },
      onSuccess: (platform) => {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.all(),
        });
        onSuccess && onSuccess(platform);
      },
    });
  };

  const useLinkMerchantToPlatform = (
    onSuccess?: (platformId: string) => void
  ) => {
    return useMutation<
      string,
      ErrorResponse,
      { platformId: string; merchantId: string }
    >({
      mutationKey: queryKeys.linkMerchantToPlatform(),
      mutationFn: async ({
        platformId,
        merchantId,
      }: {
        platformId: string;
        merchantId: string;
      }) => {
        await linkMerchantToPlatform(platformId, merchantId);
        return platformId;
      },
      onSuccess: (platformId) => {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.all(),
        });
        onSuccess && onSuccess(platformId);
      },
    });
  };

  const useDeletePendingMerchant = (onSuccess?: () => void) => {
    return useMutation<undefined, ErrorResponse, { merchantId: string }>({
      mutationKey: queryKeys.deletePendingMerchant(),
      mutationFn: async ({ merchantId }: { merchantId: string }) => {
        await deletePendingMerchant(merchantId);
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.all(),
        });
        onSuccess && onSuccess();
      },
    });
  };

  const useDeletePlatform = (onSuccess?: () => void) => {
    return useMutation<unknown, ErrorResponse, string>({
      mutationKey: queryKeys.deletePlatform(),
      mutationFn: (platformId: string) => deletePlatform(platformId),
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.all(),
        });
        onSuccess && onSuccess();
      },
    });
  };

  const useCreatePendingMerchant = (
    onSuccess?: ({
      merchantId,
      platformId,
    }: {
      merchantId: string;
      platformId: string;
    }) => void
  ) => {
    return useMutation<
      { merchantId: string; platformId: string },
      ErrorResponse,
      string
    >({
      mutationKey: queryKeys.createPendingMerchant(),
      mutationFn: async (platformId: string) => {
        const { merchantId } = await createPendingMerchant(platformId);
        return { merchantId, platformId };
      },
      onSuccess: (data) => {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.all(),
        });
        onSuccess && onSuccess(data);
      },
    });
  };

  return {
    getPlatformList: useGetPlatformList,
    addPlatform: useAddPlatform,
    updatePlatform: useUpdatePlatform,
    linkMerchantToPlatform: useLinkMerchantToPlatform,
    deletePlatform: useDeletePlatform,
    createPendingMerchant: useCreatePendingMerchant,
    deletePendingMerchant: useDeletePendingMerchant,
  };
};
